import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Home, MessageCircle, Settings, Zap } from "lucide-react";
import AppLiveClock from "../ui/AppLiveClock";
import ShareIconButton from "../ShareIconButton";
import { fmtCash, fmtFull } from "../../utils/betDetailHelpers";
import {
  buildHouseShareLink,
  buildHouseShareText,
} from "../../utils/houseShareHelpers";
import "../../share-icon.css";
import { HOME_PATH } from "../../config/products";

const APP_HOME = HOME_PATH;

const houseInitials = (name = "") => {
  const trimmed = name.trim();
  if (!trimmed) return "??";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const DetailShell = ({
  house,
  houseName,
  canManage,
  fixtureCount = 0,
  onChat,
  onPrimary,
  stats,
  isFollowing,
  onFollowClick,
  followDisabled,
  onNotify,
}) => {
  const showStats = Boolean(stats);
  const initials = useMemo(() => houseInitials(houseName), [houseName]);
  const [shareToast, setShareToast] = useState(null);
  const statItems = useMemo(() => {
    if (!stats) return [];
    return [
      { label: "Pool", value: fmtFull(stats.pool), title: fmtCash(stats.pool) },
      { label: "Pending", value: fmtFull(stats.pending), title: fmtCash(stats.pending) },
      { label: "Players", value: fmtFull(stats.players) },
      { label: "Followers", value: fmtFull(stats.followers) },
      {
        label: fixtureCount === 1 ? "Fixture" : "Fixtures",
        value: fmtFull(fixtureCount),
      },
    ];
  }, [stats, fixtureCount]);

  const shareHouse = house || { name: houseName, _id: null };
  const shareUrl = buildHouseShareLink(shareHouse._id);
  const notify = (payload) => {
    onNotify?.(payload);
    if (!onNotify && payload?.message) {
      setShareToast(payload.message);
      window.setTimeout(() => setShareToast(null), 2000);
    }
  };

  return (
    <header className="bd-chrome">
      <div className="bd-chrome-row">
        <div className="bd-chrome-lead">
          <div className="bd-chrome-house-card">
            <span className="bd-chrome-house-mark" aria-hidden>
              {initials}
            </span>
            <div className="bd-chrome-house-copy">
              <p className="bd-chrome-house-eyebrow">House detail</p>
              <h1
                className="bd-chrome-house-name bondhouse-name truncate"
                title={houseName}
              >
                {houseName || "Bet house"}
              </h1>
            </div>
          </div>
        </div>

        {showStats ? (
          <div className="bd-chrome-meta" aria-label="House summary">
            {statItems.map((item) => (
              <span
                className="bd-chrome-meta-item"
                key={item.label}
                title={item.title || undefined}
              >
                <span className="bd-chrome-meta-label">{item.label}</span>
                <span className="bd-chrome-meta-value tabular-nums">
                  {item.value}
                </span>
              </span>
            ))}
          </div>
        ) : null}

        <div className="bd-chrome-center app-chrome-clock-center">
          <AppLiveClock className="app-chrome-clock" />
        </div>

        <div className="bd-chrome-actions">
          {showStats ? (
            <button
              type="button"
              className={`bd-chrome-btn bd-chrome-btn-follow ${
                isFollowing ? "bd-chrome-btn-follow-active" : ""
              }`}
              onClick={onFollowClick}
              disabled={followDisabled}
              aria-pressed={isFollowing}
              aria-label={isFollowing ? "Following" : "Follow house"}
              title={isFollowing ? "Following" : "Follow"}
            >
              <Heart
                size={14}
                strokeWidth={2.4}
                fill={isFollowing ? "currentColor" : "none"}
              />
            </button>
          ) : null}
          <button
            type="button"
            className="bd-chrome-btn"
            onClick={onChat}
            aria-label="Open chat"
          >
            <MessageCircle size={14} strokeWidth={2.3} />
          </button>
          {shareUrl ? (
            <ShareIconButton
              className="bd-chrome-btn bd-chrome-share"
              size={14}
              url={shareUrl}
              text={buildHouseShareText(shareHouse)}
              title={houseName || "BetBond house"}
              ariaLabel="Share house"
              onShared={(mode) =>
                notify({
                  message: mode === "copied" ? "Link copied." : "Shared.",
                })
              }
              onError={(message) => notify({ message })}
            />
          ) : null}
          <Link
            to={APP_HOME}
            className="bd-chrome-btn bd-chrome-home"
            aria-label="Back to House"
            title="House"
          >
            <Home size={14} strokeWidth={2.3} aria-hidden />
          </Link>
          <button
            type="button"
            className={`bd-chrome-cta ${canManage ? "bd-chrome-cta-manage" : ""}`}
            onClick={onPrimary}
          >
            {canManage ? <Settings size={13} /> : <Zap size={13} />}
            <span>{canManage ? "Manage" : "Bet Now"}</span>
          </button>
        </div>
      </div>
      {shareToast ? (
        <p className="bd-chrome-share-toast" role="status">
          {shareToast}
        </p>
      ) : null}
    </header>
  );
};

export default DetailShell;
